import { produce } from 'immer';

import { RESET_STATE } from 'store/global/actions';

import { AI_PROCESSES_ACTION_TYPE } from '../actions';

const initialState = {
  aiProcesses: [],
  globalAiProcesses: [],
  tools: [],
  resources: [],
  isPendingReordering: false,
};

const aiProcessesReducer = (state = initialState, { type, payload }) =>
  produce(state, draft => {
    switch (type) {
      case AI_PROCESSES_ACTION_TYPE.GET_AI_PROCESSES:
        draft.aiProcesses = payload;
        break;

      case AI_PROCESSES_ACTION_TYPE.UPDATE_AI_PROCESS:
        draft.aiProcesses = state.aiProcesses.map(aiProcess =>
          aiProcess.id === payload.id ? payload : aiProcess,
        );
        break;

      case AI_PROCESSES_ACTION_TYPE.CREATE_AI_PROCESS:
        draft.aiProcesses = [...state.aiProcesses, payload];
        break;

      case AI_PROCESSES_ACTION_TYPE.DELETE_AI_PROCESS:
        draft.aiProcesses = state.aiProcesses.filter(({ id }) => id !== payload);
        break;

      case AI_PROCESSES_ACTION_TYPE.GET_AI_PROCESS_STEPS:
        draft.aiProcesses = state.aiProcesses.map(aiProcess =>
          aiProcess.id === payload.aiProcessId
            ? {
                ...aiProcess,
                steps: payload.steps,
              }
            : aiProcess,
        );
        break;

      case AI_PROCESSES_ACTION_TYPE.CREATE_AI_PROCESS_STEP:
      case AI_PROCESSES_ACTION_TYPE.UPDATE_AI_PROCESS_STEP:
        draft.aiProcesses = state.aiProcesses.map(aiProcess =>
          aiProcess.id === payload.aiProcessId
            ? {
                ...aiProcess,
                steps: (aiProcess.steps ?? []).reduce(
                  (acc, step) => {
                    if (step.id !== payload.step.id) {
                      acc.push(step);
                    }

                    return acc;
                  },
                  [payload.step],
                ),
              }
            : aiProcess,
        );

        break;

      case AI_PROCESSES_ACTION_TYPE.DELETE_AI_PROCESS_STEP:
        draft.aiProcesses = state.aiProcesses.map(aiProcess =>
          aiProcess.id === payload.aiProcessId
            ? {
                ...aiProcess,
                steps: aiProcess.steps.filter(({ id }) => id !== payload.id),
              }
            : aiProcess,
        );
        break;

      case AI_PROCESSES_ACTION_TYPE.GET_AI_PROCESS_FORM_SCHEMA:
      case AI_PROCESSES_ACTION_TYPE.UPDATE_AI_PROCESS_FORM_SCHEMA:
        draft.aiProcesses = state.aiProcesses.map(aiProcess =>
          aiProcess.id === payload.aiProcessId
            ? {
                ...aiProcess,
                formSchema: payload.formSchema,
              }
            : aiProcess,
        );
        break;

      case AI_PROCESSES_ACTION_TYPE.DELETE_AI_PROCESS_FORM_SCHEMA:
        draft.aiProcesses = state.aiProcesses.map(aiProcess =>
          aiProcess.id === payload.aiProcessId
            ? {
                ...aiProcess,
                formSchema: null,
              }
            : aiProcess,
        );
        break;

      case AI_PROCESSES_ACTION_TYPE.GET_GLOBAL_AI_PROCESSES:
        draft.globalAiProcesses = payload;
        break;

      case AI_PROCESSES_ACTION_TYPE.ENABLE_GLOBAL_AI_PROCESS:
        draft.globalAiProcesses = state.globalAiProcesses.map(process =>
          process.id === payload ? { ...process, isEnabled: true } : process,
        );
        break;

      case AI_PROCESSES_ACTION_TYPE.DISABLE_GLOBAL_AI_PROCESS:
        draft.globalAiProcesses = state.globalAiProcesses.map(process =>
          process.id === payload ? { ...process, isEnabled: false } : process,
        );
        break;

      case AI_PROCESSES_ACTION_TYPE.CLONE_GLOBAL_AI_PROCESS:
        draft.aiProcesses = [...state.aiProcesses, payload];
        break;

      case AI_PROCESSES_ACTION_TYPE.GET_AI_PROCESS_STEPS_TOOLS:
        draft.tools = payload;
        break;

      case AI_PROCESSES_ACTION_TYPE.GET_AI_PROCESS_STEPS_RESOURCES:
        draft.resources = payload;
        break;

      case AI_PROCESSES_ACTION_TYPE.UPDATE_AI_PROCESS_STEP_ORDER:
        draft.isPendingReordering = false;
        break;

      case AI_PROCESSES_ACTION_TYPE.UPDATE_AI_PROCESS_STEP_ORDER_PENDING:
      case AI_PROCESSES_ACTION_TYPE.UPDATE_AI_PROCESS_STEP_ORDER_REJECTED:
        {
          const isRejected =
            type === AI_PROCESSES_ACTION_TYPE.UPDATE_AI_PROCESS_STEP_ORDER_REJECTED;

          draft.aiProcesses = state.aiProcesses.map(aiProcess =>
            aiProcess.id === payload.aiProcessId
              ? {
                  ...aiProcess,
                  steps: aiProcess.steps.reduce((acc, step) => {
                    const stepToUpdate = payload.stepsToUpdate.find(({ id }) => id === step.id);

                    if (stepToUpdate) {
                      acc.push({
                        ...step,
                        orderNumber: isRejected
                          ? stepToUpdate.originalOrderNumber
                          : stepToUpdate.orderNumber,
                      });

                      return acc;
                    }

                    acc.push(step);

                    return acc;
                  }, []),
                }
              : aiProcess,
          );
          draft.isPendingReordering = !isRejected;
        }

        break;

      case AI_PROCESSES_ACTION_TYPE.RESET_AI_PROCESSES_STATE:
      case RESET_STATE:
        return initialState;

      default:
        break;
    }
  });

export default aiProcessesReducer;
